/**
 * The MCP service: the consent step, and the list of connected apps.
 *
 * Two different things live here because they use two different backends. The
 * consent POST goes to the MCP service (only it can mint an authorization
 * code), while the connected-apps list reads `mcp_tokens` from PocketBase
 * directly, where the API rules already scope it to the signed-in owner.
 */

import { pb } from './pb'

const MCP_URL = import.meta.env.VITE_MCP_URL ?? 'http://127.0.0.1:8100'

/** The authorize parameters the MCP service hands to the consent screen. */
export interface ConsentRequest {
  client_id: string
  client_name: string
  redirect_uri: string
  code_challenge: string
  scope: string
  state: string
  resource: string
}

/** A live grant, as Settings shows it. The token hashes are hidden fields. */
export interface ConnectedApp {
  id: string
  client_name: string
  scope: string
  created: string
  last_used: string
  access_expires_at: string
  refresh_expires_at: string
}

/**
 * Read the authorize parameters out of the URL.
 *
 * Returns null if anything required is missing, which means the user reached
 * /authorize some other way — by bookmarking it, most likely. Nothing is
 * validated for correctness here: the MCP service checked the client and the
 * redirect_uri before it sent the browser over, and it re-checks both when the
 * consent POST arrives. The SPA only carries them.
 */
export function readConsentRequest(params: URLSearchParams): ConsentRequest | null {
  const clientId = params.get('client_id')
  const redirectUri = params.get('redirect_uri')
  const challenge = params.get('code_challenge')
  if (!clientId || !redirectUri || !challenge) return null

  return {
    client_id: clientId,
    client_name: params.get('client_name') || 'An application',
    redirect_uri: redirectUri,
    code_challenge: challenge,
    scope: params.get('scope') || 'reminders:read',
    state: params.get('state') ?? '',
    resource: params.get('resource') ?? '',
  }
}

/**
 * Approve the request and get the URL to send the client back to.
 *
 * The PocketBase token goes out here, to the MCP service, and nowhere else —
 * it is exchanged immediately for a scoped grant, and the MCP endpoint itself
 * never sees one.
 */
export async function grantConsent(request: ConsentRequest): Promise<string> {
  const response = await fetch(`${MCP_URL}/oauth/consent`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${pb.authStore.token}`,
    },
    body: JSON.stringify(request),
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new Error(detail || `Could not complete authorization (${response.status})`)
  }
  const data = (await response.json()) as { redirect_to?: string }
  if (!data.redirect_to) throw new Error('The authorization server did not return a redirect')
  return data.redirect_to
}

/**
 * Where to send the client when the user declines.
 *
 * RFC 6749 §4.1.2.1: a refusal is reported to the client rather than left to
 * time out, so it can say "you declined" instead of hanging.
 */
export function denialUrl(request: ConsentRequest): string {
  const query = new URLSearchParams({ error: 'access_denied' })
  if (request.state) query.set('state', request.state)
  return `${request.redirect_uri}${request.redirect_uri.includes('?') ? '&' : '?'}${query}`
}

export async function listConnectedApps(): Promise<ConnectedApp[]> {
  const records = await pb.collection('mcp_tokens').getFullList({ sort: '-created' })
  return records as unknown as ConnectedApp[]
}

/**
 * Revoke a grant.
 *
 * Deleting the row is the whole revocation: the MCP service resolves every
 * bearer token against this table on every request and caches nothing, so the
 * app loses access on its next call.
 */
export async function disconnectApp(id: string): Promise<void> {
  await pb.collection('mcp_tokens').delete(id)
}

/** What a scope string actually permits, in words a person can check. */
export function describeScopes(scope: string): string[] {
  const scopes = scope.split(' ').filter(Boolean)
  const lines = ['See everything you track: names, dates, costs and notes']
  if (scopes.includes('reminders:write')) {
    lines.push('Add new records, and change or archive existing ones')
  }
  return lines
}

export function canWrite(scope: string): boolean {
  return scope.split(' ').includes('reminders:write')
}
